<template>
  <div class="relative">
    <div class="absolute bottom-0 right-0 z-10">
      <sw-dropdown
        :close-on-select="true"
        max-height="220"
        position="bottom-end"
        class="mb-2"
        v-if="fieldList.length >0"
      >
        <sw-button
          slot="activator"
          variant="primary-outline"
          type="button"
          class="mr-2"
        >
          <plus-sm-icon class="h-5 mr-1 -ml-2" />
          {{ $t('settings.customization.addresses.insert_fields') }}
        </sw-button>
        <div class="flex p-2">
          <ul v-for="(type, index) in fieldList" :key="index" class="list-none">
            <li class="mb-1 ml-2 text-xs font-semibold text-gray-500 uppercase">
              {{ type.label }}
            </li>
            <li
              v-for="(field, index) in type.fields"
              :key="index"
              class="w-48 text-sm font-normal cursor-pointer hover:bg-gray-200"
              @click="insertField(field.value)"
            >
              <div class="flex">
                <chevron-double-right-icon class="h-3 mt-1 text-gray-400" />{{
                  field.label
                }}
              </div>
            </li>
          </ul>
        </div>
      </sw-dropdown>
    </div>
    <sw-editor
      v-model="inputValue"
      :set-editor="inputValue"
      :disabled="disabled"
      :invalid="isFieldValid"
      :placeholder="placeholder"
      variant="header-editor"
      input-class="border-none"
      class="text-area-field"
      @input="handleInput"
      @change="handleChange"
      @keyup="handleKeyupEnter"
    />
  </div>
</template>

<script>
import { PlusSmIcon } from '@vue-hero-icons/outline'
import { ChevronDoubleRightIcon } from '@vue-hero-icons/solid'
import { mapActions, mapGetters } from 'vuex'
import customFields from '../../mixins/customFields'

export default {
  components: {
    PlusSmIcon,
    ChevronDoubleRightIcon,
  },
  props: {
    value: {
      type: [String, Number, File],
      default: '',
    },
    types: {
      type: Array,
      default: null,
    },
    placeholder: {
      type: String,
      default: '',
    },
    rows: {
      type: String,
      default: '10',
    },
    cols: {
      type: String,
      default: '30',
    },
    invalid: {
      type: Boolean,
      default: false,
    },
    fields: {
      type: Array,
      default: null,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      fieldList: [],
      invoiceFields: [],
      estimateFields: [],
      paymentFields: [],
      userFields: [],
      recapFields : [],
      documentFields : [],
      position: null,
      inputValue: this.value,
    }
  },
  computed: {
    ...mapGetters('customFields', ['getCustomFields']),
    isFieldValid() {
      return this.invalid
    },
  },
  watch: {
    value() {
      this.inputValue = this.value
    },
    fields() {
      if (this.fields && this.fields.length > 0) {
        this.getFields()
      }
    },
    getCustomFields(newValue) {
      this.invoiceFields = newValue
        ? newValue.filter((field) => field.model_type === 'Invoice')
        : []
      this.paymentFields = newValue
        ? newValue.filter((field) => field.model_type === 'Payment')
        : []
      this.estimateFields = newValue.filter(
        (field) => field.model_type === 'Estimate'
      )
      this.getFields()
    },
  },
  async mounted() {
    this.getFields()
    await this.fetchNoteCustomFields({ limit: 'all' })
  },
  methods: {
    ...mapActions('customFields', ['fetchNoteCustomFields']),

    getFields() {
      this.fieldList = []

      if (this.fields && this.fields.length > 0) {
        if (this.fields.find((field) => field == 'shipping')) {
          this.fieldList.push({
            label: 'Adresse de chargement',
            fields: [
              { label: 'Adresse', value: 'SHIPPING_ADDRESS_NAME' },
              { label: 'Pays', value: 'SHIPPING_COUNTRY' },
              { label: 'État', value: 'SHIPPING_STATE' },
              { label: 'Ville', value: 'SHIPPING_CITY' },
              { label: 'Adresse rue 1', value: 'SHIPPING_ADDRESS_STREET_1' },
              { label: 'Adresse rue 2', value: 'SHIPPING_ADDRESS_STREET_2' },
              { label: 'Téléphone', value: 'SHIPPING_PHONE' },
              { label: 'Code postal', value: 'SHIPPING_ZIP_CODE' },
            ],
          })
        }

        if (this.fields.find((field) => field == 'billing')) {
          this.fieldList.push({
            label: 'Adresse de facturation',
            fields: [
              { label: 'Adresse', value: 'BILLING_ADDRESS_NAME' },
              { label: 'Pays', value: 'BILLING_COUNTRY' },
              { label: 'État', value: 'BILLING_STATE' },
              { label: 'Ville', value: 'BILLING_CITY' },
              { label: 'Adresse rue 1', value: 'BILLING_ADDRESS_STREET_1' },
              { label: 'Adresse rue 2', value: 'BILLING_ADDRESS_STREET_2' },
              { label: 'Téléphone', value: 'BILLING_PHONE' },
              { label: 'Code postal', value: 'BILLING_ZIP_CODE' },
            ],
          })
        }

        if (this.fields.find((field) => field == 'user')) {
          this.fieldList.push({
            label: 'Partenaire',
            fields: [
              { label: 'Nom', value: 'USER_NAME' },
              { label: 'Email', value: 'USER_EMAIL' },
              { label: 'Téléphone', value: 'USER_PHONE' },
              ...this.userFields.map((i) => ({
                label: i.label,
                value: i.slug,
              })),
            ],
          })
        }

        if (this.fields.find((field) => field == 'typeDocument')) {
          this.fieldList.push({
            label: 'Partenaire',
            fields: [
              { label: 'Nom', value: 'TYPE_NAME' },
              { label: 'Validité', value: 'TYPE_VALIDITY' },
              ...this.typeDocumentsFields.map((i) => ({
                label: i.label,
                value: i.slug,
              })),
            ],
          })
        }

        if (this.fields.find((field) => field == 'document')) {
          this.fieldList.push({
            label: 'Document',
            fields: [
              { label: 'Date délivrance', value: 'DOCUMENT_DATE' },
              { label: 'Type document', value: 'DOCUMENT_TYPE_NAME' },
              { label: 'Validité', value: 'DOCUMENT_TYPE_VALIDITY' },
              ...this.documentFields.map((i) => ({
                label: i.label,
                value: i.slug,
              })),
            ],
          })
        }

        if (this.fields.find((field) => field == 'recap')) {
          this.fieldList.push({
            label: 'Récap',
            fields: [
              { label: 'Date Récap', value: 'RECAP_DATE' },
              ...this.recapFields.map((i) => ({
                label: i.label,
                value: i.slug,
              })),
            ],
          })
        }

        if (this.fields.find((field) => field == 'invoice')) {
          this.fieldList.push({
            label: 'Facture',
            fields: [
              { label: 'Date', value: 'INVOICE_DATE' },
              { label: 'Date d\'échéance', value: 'INVOICE_DUE_DATE' },
              { label: 'Numméro', value: 'INVOICE_NUMBER' },
              { label: 'Numéro de réf.', value: 'INVOICE_REF_NUMBER' },
              { label: 'Lien de facturation', value: 'INVOICE_LINK' },
              ...this.invoiceFields.map((i) => ({
                label: i.label,
                value: i.slug,
              })),
            ],
          })
        }

        if (this.fields.find((field) => field == 'estimate')) {
          this.fieldList.push({
            label: 'Devis',
            fields: [
              { label: 'Date', value: 'ESTIMATE_DATE' },
              { label: 'Date d\'expiration', value: 'ESTIMATE_EXPIRY_DATE' },
              { label: 'Numéro', value: 'ESTIMATE_NUMBER' },
              { label: 'Numéro de réf.', value: 'ESTIMATE_REF_NUMBER' },
              { label: 'Lien de devis', value: 'ESTIMATE_LINK' },
              ...this.estimateFields.map((i) => ({
                label: i.label,
                value: i.slug,
              })),
            ],
          })
        }

        if (this.fields.find((field) => field == 'payment')) {
          this.fieldList.push({
            label: 'Paiement',
            fields: [
              { label: 'Date', value: 'PAYMENT_DATE' },
              { label: 'Numéro', value: 'PAYMENT_NUMBER' },
              { label: 'Moyen de paiement', value: 'PAYMENT_MODE' },
              { label: 'Montant', value: 'PAYMENT_AMOUNT' },
              { label: 'Lien du paiement', value: 'PAYMENT_LINK' },
              ...this.paymentFields.map((i) => ({
                label: i.label,
                value: i.slug,
              })),
            ],
          })
        }

        if (this.fields.find((field) => field == 'company')) {
          this.fieldList.push({
            label: 'Société',
            fields: [
              { label: 'Nom de la ste', value: 'COMPANY_NAME' },
              { label: 'Pays', value: 'COMPANY_COUNTRY' },
              { label: 'État', value: 'COMPANY_STATE' },
              { label: 'Ville', value: 'COMPANY_CITY' },
              { label: 'Adresse rue 1', value: 'COMPANY_ADDRESS_STREET_1' },
              { label: 'Adresse rue 2', value: 'COMPANY_ADDRESS_STREET_2' },
              { label: 'Téléphone', value: 'COMPANY_PHONE' },
              { label: 'Code postal', value: 'COMPANY_ZIP_CODE' },
              { label: 'Email', value: 'COMPANY_EMAIL'}
            ],
          })
        }
      }
    },

    insertField(varName) {
      if (this.inputValue) {
        this.inputValue += `{${varName}}`
      } else {
        this.inputValue = `{${varName}}`
      }
      this.$emit('input', this.inputValue)
    },

    handleInput(e) {
      this.$emit('input', this.inputValue)
    },

    handleChange(e) {
      this.$emit('change', this.inputValue)
    },

    handleKeyupEnter(e) {
      this.$emit('keyup', this.inputValue)
    },

    handleKeyDownEnter(e) {
      this.$emit('keydown', e, this.inputValue)
    },

    handleFocusOut(e) {
      this.$emit('blur', this.inputValue)
    },
  },
}
</script>
